import { Injectable } from "@angular/core";
import { IApplication } from "../models/IApplication";
import { IInteraction } from "../models/IInteraction";
import { ApplicationService } from "./application.service";
import { IAnalyticsStrategy } from "../models/strategies";
import { IRecorderInfo, IRecorderSessionInfo } from "../models/IRecorder";
import {
  InteractionService as PerfomanceInteractionService,
  IInteractionFilters,
  InteractionFilterRecording,
} from "@auvious/interaction";
import { PagedCollection } from "@auvious/common";
import { InteractionMetrics } from "../models/InteractionMetrics";
import { InteractionMetric, InteractionMetricEnum } from "../core-ui.enums";
import {
  IAppointment,
  IAppointmentRouting,
  IEndpointMetadata,
  PublicParam,
} from "../models";
import { IEndpoint } from "@auvious/rtc";
import { ConversationTypeEnum } from "@auvious/integrations";
import { AppConfigService } from "./app.config.service";
import { MediaRulesService } from "./media.rules.service";
import { GenericErrorHandler } from "./error-handlers.service";
import { AppointmentService } from "./appointment.service";

@Injectable()
export class AnalyticsService implements IAnalyticsStrategy {
  constructor(
    private applicationService: ApplicationService,
    private interactionService: PerfomanceInteractionService,
    private appointmentService: AppointmentService,
    private errorHandler: GenericErrorHandler,
    // we should use mediaRules but we have a circular dependency
    private config: AppConfigService
  ) {}

  private get application(): IApplication {
    return this.applicationService.getActiveApplication();
  }

  private get impl(): IAnalyticsStrategy {
    return this.application.analyticsStrategy();
  }

  trackCallRequested(
    interaction: IInteraction,
    extra: Partial<{ supervisorUrl: string; persistOnIntegration: boolean }> = {
      persistOnIntegration: true,
    }
  ): Promise<string> {
    extra.persistOnIntegration ??= true;
    if (extra.persistOnIntegration) {
      this.impl.trackCallRequested(interaction, extra);
    }
    return this.createInteractionMetrics(interaction);
  }

  trackCobrowseRequested(
    interaction: IInteraction,
    endpoint: IEndpoint<IEndpointMetadata>
  ): void {
    this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.cobrowseViewRequesteddAt]:
        new Date().toISOString(),
      [InteractionMetricEnum.roomName]: interaction.getRoom(),
      [InteractionMetricEnum.originUrl]: endpoint.metadata?.originUrl,
      [InteractionMetricEnum.customerDisplayName]: endpoint.metadata?.name,
      [InteractionMetricEnum.customerMetadata]:
        endpoint.metadata?.customerMetadata,
      [InteractionMetricEnum.cobrowseRequestType]: endpoint.metadata?.type,
    });
  }

  trackInvitationSent(interaction: IInteraction): void {
    this.impl.trackInvitationSent(interaction);
    this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.ticketSentAt]: new Date().toISOString(),
      [InteractionMetricEnum.roomName]: interaction.getRoom(),
      // we set it to null in case the agent closed one call, started another (for the same conversation)
      // the supervisor/whisper would not join because we see a call-end
      [InteractionMetricEnum.callEndAt]: null,
    });
  }

  async trackAppointmentStarted(
    interaction: IInteraction,
    appointment: IAppointment<IAppointmentRouting>
  ): Promise<void> {
    const participants = await Promise.all(
      appointment.participants
        .filter((p) => p.type === "CUSTOMER")
        .map((p) =>
          this.appointmentService.getParticipantDetails(appointment.id, p.id)
        )
    );

    this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.roomName]: interaction.getRoom(),
      [InteractionMetricEnum.appointmentId]: appointment.id,
      [InteractionMetricEnum.appointmentParticipants]: participants.map((p) => {
        delete p.ticket;
        return p;
      }),
    });
  }

  trackCallEnding(interaction: IInteraction): Promise<void> {
    return this.impl.trackCallEnding(interaction);
  }

  trackCallTransferred(interaction: IInteraction): Promise<void> {
    this.impl.trackCallTransferred(interaction);
    return this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.callTransferredAt]: new Date().toISOString(),
    });
  }

  async trackCallEnded(
    interaction: IInteraction,
    persistOnIntegration: boolean = true
  ): Promise<void> {
    if (persistOnIntegration) {
      await this.impl.trackCallEnded(interaction);
    }
    return this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.callEndAt]: new Date().toISOString(),
    });
  }

  async trackConferenceRoomEnded(interaction: IInteraction): Promise<void> {
    return this.impl.trackConferenceRoomEnded(interaction);
  }

  trackRecordingStarted(
    recorder: IRecorderInfo,
    interaction: IInteraction,
    type: "media" | "co-browse" = "media"
  ): void {
    this.impl.trackRecordingStarted(recorder, interaction, type);
    let metrics: InteractionMetric;
    switch (type) {
      case "media":
        metrics = {
          [InteractionMetricEnum.recorderId]: recorder.recorderId,
          [InteractionMetricEnum.recorderInstanceId]: recorder.instanceId,
        };
        break;
      case "co-browse":
        metrics = {
          [InteractionMetricEnum.cobrowseRecorderId]: recorder.recorderId,
          [InteractionMetricEnum.cobrowseRecorderInstanceId]:
            recorder.instanceId,
        };
        break;
    }
    this.updateInteractionMetrics(interaction, metrics);
  }

  trackRecordingStopped(
    session: IRecorderSessionInfo,
    interaction: IInteraction,
    logToImplementation = true
  ): void {
    if (logToImplementation) {
      this.impl.trackRecordingStopped(session, interaction);
    }
    this.updateInteractionMetrics(interaction, {
      [InteractionMetricEnum.recordingUrl]: session.url,
      [InteractionMetricEnum.recorderStorageProvider]: session.provider,
    });
  }

  async updateInteractionMetrics(
    interaction: IInteraction,
    data: InteractionMetric,
    append: boolean = false
  ): Promise<void> {
    const maxRetries = 3; // Configure max retry attempts
    let attempts = 0;
    while (attempts < maxRetries) {
      try {
        const metric = await this.getInteractionMetrics(interaction.getId());
        for (const key in data) {
          if (key in data) {
            switch (key) {
              case InteractionMetricEnum.customerMetadata:
                if (
                  this.config.publicParam(PublicParam.CUSTOMER_METADATA_ENABLED)
                ) {
                  metric.setMetric(
                    key as InteractionMetricEnum,
                    MediaRulesService.filterCustomerMetadata(
                      this.config.publicParam(
                        PublicParam.CUSTOMER_METADATA_BLACKLIST
                      ),
                      { ...metric.getCustomerMetadata(), ...data[key] }
                    )
                  );
                }
                break;
              case InteractionMetricEnum.tags:
                if (append) {
                  metric.setMetric(InteractionMetricEnum.tags, [
                    ...metric.getMetric(InteractionMetricEnum.tags),
                    ...(data[key] as string[]),
                  ]);
                } else {
                  metric.setMetric(InteractionMetricEnum.tags, data[key]);
                }
                break;
              default:
                metric.setMetric(key as InteractionMetricEnum, data[key]);
                break;
            }
          }
        }

        await this.interactionService.update(
          metric.getId(),
          metric.getCallType(),
          metric.getMetrics()
        );

        // If update succeeds, exit the retry loop
        return;
      } catch (ex) {
        // Check if it's a conflict error
        if (ex?.response?.status === 409) {
          attempts++;

          // If we haven't exceeded max retries, continue to next iteration
          if (attempts < maxRetries) {
            continue;
          }
        }

        return;
        // For non-409 errors or if max retries exceeded, throw the error
        // throw new Error(`Failed to update interaction metrics: ${ex.message}`);
      }
    }

    return;
    // If we exit the loop without returning, we've exceeded max retries
    // throw new Error(
    //   "Failed to update interaction metrics after maximum retry attempts"
    // );
  }

  // if (ex.response.status === 409) {
  //   // version conflict. get latest version and update
  //   return this.updateInteractionMetrics(interaction, data);
  // }

  async createInteractionMetrics(interaction: IInteraction) {
    try {
      const exists = await this.interactionService.get(interaction.getId());
      if (exists) {
        return;
      }
    } catch (ex) {
      // do nothing
    }
    return this.interactionService.create(
      interaction.getId(),
      interaction.getType(),
      {
        [InteractionMetricEnum.integrationInteractionAvailable]:
          interaction.isIntegrationInteractionAvailable(),
        [InteractionMetricEnum.callStartAt]: new Date().toISOString(),
        [InteractionMetricEnum.callOrigin]: interaction.getOrigin(),
        [InteractionMetricEnum.callOriginMode]: interaction.getOriginMode(),
        [InteractionMetricEnum.callDestination]: interaction.getDestination(),
        // conference_id
        [InteractionMetricEnum.roomName]:
          interaction.getType() === ConversationTypeEnum.callback
            ? interaction.getRoom()
            : null,
        [InteractionMetricEnum.customerId]: interaction.getCustomerId(),
        [InteractionMetricEnum.customerMetadata]: this.config.publicParam(
          PublicParam.CUSTOMER_METADATA_ENABLED
        )
          ? MediaRulesService.filterCustomerMetadata(
              this.config.publicParam(PublicParam.CUSTOMER_METADATA_BLACKLIST),
              interaction.getMetadata()
            )
          : undefined,
      }
    );
  }

  async queryInteractionMetrics(
    query: IInteractionFilters,
    page: number,
    pageSize: number = 20
  ): Promise<PagedCollection<InteractionMetrics>> {
    try {
      const data = await this.interactionService.getPage(
        query.startAt,
        query.endAt,
        page,
        pageSize,
        "createdAt,desc",
        query
      );
      const content = data.content.map((m) => new InteractionMetrics(m));
      return {
        ...data,
        content,
      };
    } catch (ex) {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }

  async getInteractionMetrics(
    interactionId: string
  ): Promise<InteractionMetrics> {
    try {
      const data = await this.interactionService.get(interactionId);
      return new InteractionMetrics(data);
    } catch (ex) {
      this.errorHandler.handleNotAuthenticatedError(ex);
      throw ex;
    }
  }
}
